import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {
  type CertificateAttributes,
  type CommitSignatureType,
  type SignatureResult,
  SignedCommitBadge,
  type VerificationStatus,
} from '@github-ui/signed-commit-badge'
import {Suspense} from 'react'
import {graphql, useFragment, usePreloadedQuery} from 'react-relay'

import {VALUES} from '../constants/values'
import type {
  ReferencedEventVerificationStatusInner$data,
  ReferencedEventVerificationStatusInner$key,
} from './__generated__/ReferencedEventVerificationStatusInner.graphql'
import type {secondaryTimelineQuery} from './__generated__/secondaryTimelineQuery.graphql'
import {ReferencedEventVerificationStatusFailure} from './ReferencedEventVerificationStatusFailure'
import {ReferencedEventVerificationStatusSkeleton} from './ReferencedEventVerificationStatusSkeleton'
import {type PreloadedSecondaryQueryType, SecondaryTimelineGraphqlQuery} from './secondary-timeline-query'

type SignatureResultFromFragment = NonNullable<ReferencedEventVerificationStatusInner$data['signature']>
type CertificateAttributesFromFragment = NonNullable<NonNullable<SignatureResultFromFragment>['subject']>

type ReferencedEventVerificationStatusProps = {
  secondaryTimelineQueryRef?: PreloadedSecondaryQueryType
  eventId: string
  viewerLogin: string | null
}

export function ReferencedEventVerificationStatus({
  secondaryTimelineQueryRef,
  eventId,
  viewerLogin,
}: ReferencedEventVerificationStatusProps): React.ReactElement | null {
  if (!secondaryTimelineQueryRef) return null
  return (
    <ErrorBoundary fallback={<ReferencedEventVerificationStatusFailure />}>
      <Suspense fallback={<ReferencedEventVerificationStatusSkeleton />}>
        <ReferencedEventVerificationStatusQuery
          secondaryTimelineQueryRef={secondaryTimelineQueryRef}
          eventId={eventId}
          viewerLogin={viewerLogin}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

function ReferencedEventVerificationStatusQuery({
  secondaryTimelineQueryRef,
  eventId,
  viewerLogin,
}: {
  secondaryTimelineQueryRef: PreloadedSecondaryQueryType
  eventId: string
  viewerLogin: string | null
}): React.ReactElement | null {
  const {nodes} = usePreloadedQuery<secondaryTimelineQuery>(SecondaryTimelineGraphqlQuery, secondaryTimelineQueryRef)
  const commitNode = nodes?.find(node => node?.__id === eventId)

  if (!commitNode || !commitNode.commit) return null

  return <ReferencedEventVerificationStatusInner commitRef={commitNode.commit} viewerLogin={viewerLogin} />
}

function ReferencedEventVerificationStatusInner({
  commitRef,
  viewerLogin,
}: {
  commitRef: ReferencedEventVerificationStatusInner$key
  viewerLogin: string | null
}) {
  const commit = useFragment(
    graphql`
      fragment ReferencedEventVerificationStatusInner on Commit {
        abbreviatedOid
        hasSignature
        verificationStatus
        signature {
          __typename
          signer {
            login
            avatarUrl
          }
          state
          wasSignedByGitHub
          ... on SmimeSignature {
            issuer {
              commonName
              emailAddress
              organization
              organizationUnit
            }
            subject {
              commonName
              emailAddress
              organization
              organizationUnit
            }
          }
          ... on GpgSignature {
            keyId
          }
          ... on SshSignature {
            keyFingerprint
          }
        }
      }
    `,
    commitRef,
  )

  if (
    !commit ||
    !commit.signature ||
    !commit.verificationStatus ||
    commit.verificationStatus === 'UNSIGNED' ||
    !commit.abbreviatedOid ||
    commit.hasSignature === undefined
  ) {
    return null
  }
  return (
    <SignedCommitBadge
      commitOid={commit.abbreviatedOid}
      hasSignature={commit.hasSignature}
      verificationStatus={getVerificationStatus(commit.verificationStatus)}
      signature={getSignatureResult(commit.signature, viewerLogin)}
    />
  )
}

function getVerificationStatus(verificationStatus: string): VerificationStatus {
  switch (verificationStatus) {
    case 'VERIFIED':
      return 'verified'
    case 'UNVERIFIED':
      return 'unverified'
    case 'PARTIALLY_VERIFIED':
      return 'partially_verified'
    default:
      return 'unsigned'
  }
}

function getSignatureResult(signature: SignatureResultFromFragment, viewerLogin: string | null): SignatureResult {
  return {
    hasSignature: true,
    helpUrl: VALUES.commitBadgeHelpUrl,
    isViewer: !!signature.signer && viewerLogin === signature.signer.login,
    keyExpired: signature.state === 'EXPIRED_KEY',
    // keyId isn't used for S/MIME signatures
    keyId: (signature.__typename === 'GpgSignature' ? signature.keyId : signature.keyFingerprint) ?? '',
    keyRevoked: signature.state === 'OCSP_REVOKED',
    signedByGitHub: signature.wasSignedByGitHub,
    signerLogin: signature.signer?.login ?? '',
    signerAvatarUrl: signature.signer?.avatarUrl ?? '',
    signatureType: signature.__typename as CommitSignatureType,
    signatureCertificateSubject: toCertificateAttributes(signature.subject),
    signatureCertificateIssuer: toCertificateAttributes(signature.issuer),
    signatureVerificationReason: signature.state,
  }
}

function toCertificateAttributes(attributes?: CertificateAttributesFromFragment | null): CertificateAttributes {
  if (!attributes) {
    return {}
  }

  return {
    common_name: attributes.commonName ?? undefined,
    email_address: attributes.emailAddress ?? undefined,
    organization: attributes.organization ?? undefined,
    organization_unit: attributes.organizationUnit ?? undefined,
  }
}

try{ ReferencedEventVerificationStatus.displayName ||= 'ReferencedEventVerificationStatus' } catch {}
try{ ReferencedEventVerificationStatusQuery.displayName ||= 'ReferencedEventVerificationStatusQuery' } catch {}
try{ ReferencedEventVerificationStatusInner.displayName ||= 'ReferencedEventVerificationStatusInner' } catch {}